/* @flow */
/**
 * Manages feature-flag configuration lists, provider state, dependencies, and related API actions.
 */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import request from '../../services/request';
import { toggleSpinner } from '../dialogStates/dialogStatesSlice';
import { cryptoRand } from '../../helpers/common';
import appConfig from '../../appConfig';

const name = 'featureFlag';

function createInitialState() {
  return {
    selectedFilters: {
      factoryCode: [],
      managingOffice: [],
      managingUserID: [],
      managingUserEmail: [],
      managingUser: []
    },
    providerData: [],
    providerList: [],
    featureFlagList: [],
    pmoConfigScreenData: null,
    unfilteredConfigScreenData: null,
    dataId: '',
    featureFlagHistorySubtableResults: {},
    addFactoryDetailDropdownValues: {
      factoryAffiliations: []
    },
    dependencyDropdownValues: {
      dependencyList: []
    },
    reloadActiveList: false,
    hasProviderDataChanged: false
  };
}

/**
 * Updates the feature-flag configuration table and keeps an unfiltered copy for filtering.
 */
function updatePMOConfigScreen(state: Object, action: Object) {
  // UPDATE_PMO_CONFIG_SCREEN_RESULTS

  const payload = {
    data: action.payload.flattenedResponse,
    filteredResults: action.payload.filteredResults || action.payload.flattenedResponse
  };

  return {
    ...state,
    pmoConfigScreenData: payload.filteredResults,
    unfilteredConfigScreenData: payload.data,
    dataId: cryptoRand().toString().slice(2, 10)
  };
}

/**
 * Stores provider feature-flag status data and records whether it changed after initial load.
 */
function updateProviderData(state: Object, action: Object) {
  // Compare serialized objects because providerData is keyed by feature tag.
  const hasChanged = state.providerData.length !== 0
    ? JSON.stringify(state.providerData) !== JSON.stringify(action.payload)
    : false;
  return {
    ...state,
    providerData: action.payload,
    hasProviderDataChanged: hasChanged
  };
}

/**
 * Stores the raw provider feature-flag list used by provider consumers.
 */
function updateProviderList(state: Object, action: Object) {
  return {
    ...state,
    providerList: action.payload
  };
}

/**
 * Stores feature flags with computed Active/Inactive status values for display.
 */
function updateFeatureFlagList(state: Object, action: Object) {
  const updatedFeatureFlagList = action.payload.map((item) => {
    // An end time later than the start time marks the flag as active in the UI.
    const status = item.endTime > item.startTime ? 'Active' : 'Inactive';
    return {
      ...item,
      status
    };
  });
  return {
    ...state,
    featureFlagList: updatedFeatureFlagList
  };
}

/**
 * Resets the feature-flag slice to a fresh initial state object.
 */
function resetToInitialState() {
  return {
    ...createInitialState()
  };
}

/**
 * Stores feature-flag history subtable rows with display status metadata.
 */
function updateFeatureTogglingHistorySubtableResults(state: Object, action: Object) {
  // UPDATE_FEATURE_TOGGLING__HISTORY_SUBTABLE_RESULTS
  const updatedPayload = action.payload.data.map((item) => {
    // History rows use the same start/end time status calculation as current flags.
    const status = item.endTime > item.startTime ? 'Active' : 'Inactive';
    return {
      ...item,
      status
    };
  });
  const payload = {
    data: updatedPayload,
    dataNext: null
  };
  return {
    ...state,
    featureFlagHistorySubtableResults: payload
  };
}

/**
 * Updates factory-affiliation dropdown options for adding feature flags.
 */
function updatetFactoryAffiliationsDropdown(state: Object, action: Object) {
  return {
    ...state,
    addFactoryDetailDropdownValues: {
      ...state.addFactoryDetailDropdownValues,
      factoryAffiliations: action.payload
    }
  };
}

/**
 * Converts feature tags into dependency dropdown options.
 */
function updateDependencyDropdown(state: Object, action: Object) {
  const values = action.payload?.map((row) => ({
    id: row.featureTag,
    value: row.featureTag
  }));
  return {
    ...state,
    dependencyDropdownValues: {
      ...state.dependencyDropdownValues,
      dependencyList: values
    }
  };
}

/**
 * Toggles whether active feature lists should reload after provider data changes.
 */
function updateReloadActiveList(state: Object, action: Object) {
  return {
    ...state,
    reloadActiveList: state.hasProviderDataChanged ? action.payload : false
  };
}

/**
 * Collects feature-flag reducers for slice creation.
 */
function createReducers() {
  return {
    updatePMOConfigScreen,
    updateProviderData,
    updateProviderList,
    updateFeatureFlagList,
    resetToInitialState,
    updateFeatureTogglingHistorySubtableResults,
    updatetFactoryAffiliationsDropdown,
    updateDependencyDropdown,
    updateReloadActiveList
  };
}

export const initialState = createInitialState();
export const reducers = createReducers();
const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };

/**
 * Fetches current-version feature flags from getFeatureFlag and updates list/dependency state.
 */
function fetchFeatureFlagList() {
  return createAsyncThunk(
    `${name}/fetchFeatureFlagList`,
    async (_, { dispatch }) => {
      dispatch(toggleSpinner(true));
      // Request only configured fields and the current version records for the main list.
      const query = `?fields=${
        appConfig.featureFlagFields.join(',')
      }&filter=versionType(CURRENT)`;
      request({
        api: 'getFeatureFlag',
        method: 'get',
        routeParams: { query }
      }, dispatch).then((response) => {
        dispatch(toggleSpinner(false));
        const configData = response.data.objects;
        dispatch(actions.updateFeatureFlagList(configData));
        dispatch(actions.updateDependencyDropdown(configData));
      }).catch((error) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Fetches history-version feature flags for a row and stores history subtable results.
 */
function fetchFeatureTogglingHistorySubTable() {
  return createAsyncThunk(
    `${name}/fetchFeatureTogglingHistorySubTable`,
    async ({
      rowId, callback
    }, { dispatch }) => {
      dispatch(toggleSpinner(true));
      const query = `?fields=${
        appConfig.featureFlagFields.join(',')
      }&filter=versionType(HISTORY),featureTag(${rowId})`;
      request({
        api: 'getFeatureFlag',
        method: 'get',
        routeParams: { query },
        cancellable: true
      }, dispatch).then((response) => {
        // Keep only history rows for the requested feature tag in the expanded subtable.
        const filteredData = response.data.objects.filter(
          (x) => x.featureTag === rowId
        ).filter((item) => item.versionType === 'HISTORY');
        callback(filteredData);
        dispatch(actions.updateFeatureTogglingHistorySubtableResults({ data: filteredData }));
        dispatch(toggleSpinner(false));
      }).catch((error) => {
        callback(null, error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Fetches dropdown lookup data and maps partner numbers to factory affiliation options.
 */
function fetchFeatureFlagFactoryAffiliationsData() {
  return createAsyncThunk(
    `${name}/fetchFeatureFlagFactoryAffiliationsData`,
    async (
      { callback },
      { getState, dispatch }
    ) => {
      request(
        {
          api: 'dropdownLookup',
          method: 'get'
        },
        dispatch,
        getState
      )
        .then((response) => {
          const { partnerNumber } = response.data;
          const factoryCodeList = partnerNumber.map
            ? partnerNumber.map((option) => ({
              // Dropdowns expect both id and value to carry the partner number.
              id: option.partnerNo,
              value: option.partnerNo
            }))
            : [];
          dispatch(actions.updatetFactoryAffiliationsDropdown(factoryCodeList));
          if (callback) {
            callback(response.data);
          }
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          if (callback) {
            callback(null, error);
          }
        });
    }
  );
}

/**
 * Submits feature-flag updates to the featureFlag API after removing UI-only fields.
 */
function submitFeatureFlagListUpdate() {
  return createAsyncThunk(
    `${name}/updateFeatureFlagList`,
    async (
      {
        data,
        callback
      },
      { getState, dispatch }
    ) => {
      const cleanedData = data.map((obj) => {
        // isActive is derived for display and is not accepted by the update API.
        const { isActive, ...rest } = obj;
        // Remove endTime if it's empty or null
        if (rest.endTime === '' || rest.endTime === null) {
          const { endTime, ...restWithoutEndTime } = rest;
          return restWithoutEndTime;
        }
        return rest;
      });
      const updateRequestBody = {
        featureFlag: cleanedData
      };
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'featureFlag',
          method: 'put',
          data: updateRequestBody,
          cancellable: true
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Adds a new feature flag after normalizing form selections and date fields for the API.
 */
function addNewFeatureFlagDetails() {
  return createAsyncThunk(
    `${name}/addNewFeatureFlagDetails`,
    async (
      {
        data,
        callback
      },
      { getState, dispatch }
    ) => {
      const temp = { ...data[0] };
      Object.keys(temp).forEach((key) => {
        if (key === 'groups') {
          // Multi-select fields submit ids rather than full option objects.
          temp[key] = temp[key].map((item) => item.id);
        }
        if (key === 'dependencyFeatures') {
          temp[key] = temp[key].map((item) => item.id);
        }
        if (key === 'factoryAffiliations') {
          const factoryValues = temp[key].map((item) => item.id);
          temp[key] = factoryValues;
        }
        if (key === 'startTime') {
          temp[key] = new Date(temp[key]).toISOString();
        }
        if (key === 'endTime') {
          if (temp[key]) {
            temp[key] = new Date(temp[key]).toISOString();
          }
        }
      });
      if (temp.endTime === '' || temp.endTime === null) {
        // Optional endTime is omitted entirely when the form leaves it blank.
        delete temp.endTime;
      }
      if (temp.dependencyFeatures.length === 0) {
        delete temp.dependencyFeatures;
      }
      dispatch(toggleSpinner(true));
      request(
        {
          api: 'featureFlag',
          method: 'post',
          data: temp,
          cancellable: true
        },
        dispatch,
        getState
      )
        .then((response) => {
          dispatch(toggleSpinner(false));
          callback(response.data);
        })
        .catch((error) => {
          dispatch(toggleSpinner(false));
          callback(null, error);
        });
    }
  );
}

/**
 * Fetches active provider feature flags and stores a feature-tag keyed availability map.
 */
function fetchProviderData() {
  return createAsyncThunk(
    `${name}/fetchProviderData`,
    async (_, { dispatch }) => {
      // Provider consumers need only tag, end time, and dependency metadata.
      const query = '?fields=featureTag,endTime,dependencyFeatures&filter=isActive(true)';
      request({
        api: 'getFeatureFlag',
        method: 'get',
        routeParams: { query }
      }, dispatch).then((response) => {
        const configData = {};
        const featureFlagData = [
          ...response.data.objects,
          { featureTag: 'BREAKING_50' }
        ];
        featureFlagData
          .forEach((item) => {
            configData[item.featureTag] = item.endTime ? (
              // change it to >= to test the feature flag
              new Date(item.endTime).getTime()) > Date.now() : true;
          });
        dispatch(actions.updateProviderData(configData));
        dispatch(actions.updateProviderList(featureFlagData));
      }).catch((error) => {
        console.log('Error: ', error);
        dispatch(toggleSpinner(false));
      });
    }
  );
}

/**
 * Creates feature-flag async thunks exposed alongside slice actions.
 */
function createExtraActions() {
  return {
    fetchFeatureFlagList: fetchFeatureFlagList(),
    fetchProviderData: fetchProviderData(),
    fetchFeatureTogglingHistorySubTable: fetchFeatureTogglingHistorySubTable(),
    fetchFeatureFlagFactoryAffiliationsData: fetchFeatureFlagFactoryAffiliationsData(),
    updateFeatureFlagList: submitFeatureFlagListUpdate(),
    addNewFeatureFlagDetails: addNewFeatureFlagDetails()
  };
}

const extraActions = createExtraActions();

export const featureFlagActions = { ...actions, ...extraActions };
export const featureFlagReducer = slice.reducer;
